/*  CourseReviewInbox — Admin course-lifecycle console.
    ────────────────────────────────────────────────────────────────────────
    Route: /admin/course-reviews
    Access: admin + super_admin.

    Tabs:
      • Pending   → awaiting decision (approve / reject)
      • Published → live courses; admins can Unpublish for revisions
      • Rejected  → archive of rejected submissions with feedback trail

    Backend endpoints:
      GET  /api/admin/course-review/inbox
      GET  /api/admin/course-review/published
      GET  /api/admin/course-review/rejected
      POST /api/admin/course-review/courses/{id}/unpublish
*/
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    GraduationCap, ArrowRight, ClockCounterClockwise, CheckCircle, XCircle,
    User as UserIcon, Eye, ArrowUUpLeft, Users, Certificate, Trash, ArrowClockwise,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { promptDialog, confirmDialog } from "@/lib/confirm";


function timeAgo(iso) {
    if (!iso) return "—";
    const secs = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return `${Math.round(secs / 86400)}d ago`;
}


export default function CourseReviewInbox() {
    const [inbox, setInbox] = useState({ pending: [], counts: { pending: 0, published: 0, rejected: 0 } });
    const [published, setPublished] = useState([]);
    const [rejected, setRejected] = useState([]);
    const [deleted, setDeleted] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("pending");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [i, p, r, d] = await Promise.all([
                api.get("/admin/course-review/inbox"),
                api.get("/admin/course-review/published"),
                api.get("/admin/course-review/rejected"),
                api.get("/admin/course-review/deleted"),
            ]);
            setInbox(i.data);
            setPublished(p.data.published || []);
            setRejected(r.data.rejected || []);
            setDeleted(d.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load review console.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const prev = document.title;
        document.title = "Course Reviews — Admin";
        return () => { document.title = prev; };
    }, []);

    const handleUnpublish = useCallback(async (course) => {
        const reason = await promptDialog({
            title: `Unpublish "${course.title}"?`,
            message: "The course will be pulled back to draft. Enrolled doctors keep their progress, but new enrollments and lesson access are blocked. Please tell the author why you're pulling this course.",
            placeholder: "e.g. Module 2 lesson 3 has an incorrect diagram · outdated pricing guidance…",
            confirmText: "Unpublish",
            cancelText: "Keep live",
            tone: "warn",
            multiline: true,
            minLength: 10,
            maxLength: 500,
            required: true,
            testid: "unpublish-confirm",
        });
        if (reason == null) return;
        try {
            await api.post(`/admin/course-review/courses/${course.id}/unpublish`, { reason: reason.trim() });
            toast.success(`"${course.title}" unpublished.`);
            await load();
            setTab("pending");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't unpublish course.");
        }
    }, [load]);

    const handleAdminDelete = useCallback(async (course) => {
        const reason = await promptDialog({
            title: `Delete "${course.title}"?`,
            message: "This will permanently remove the course, its modules, lessons, and quizzes. Deletions are logged in the course activity trail — please explain why.",
            placeholder: "e.g. Author-requested removal · duplicate of another course · abandoned draft…",
            confirmText: "Delete permanently",
            cancelText: "Keep course",
            tone: "danger",
            multiline: true,
            minLength: 10,
            maxLength: 500,
            required: true,
            testid: "admin-course-delete-prompt",
        });
        if (reason == null) return;
        try {
            await api.delete(`/admin/course-review/courses/${course.id}`, { data: { reason: reason.trim() } });
            toast.success(`"${course.title}" deleted.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't delete course.");
        }
    }, [load]);

    // iter100.119 — restore soft-deleted course. Confirm dialog only (no
    // reason required — restoration is inherently a positive/undo action;
    // the earlier deletion reason is already on the record).
    const handleRestore = useCallback(async (course) => {
        const ok = await confirmDialog({
            title: `Restore "${course.title}"?`,
            message: `This will bring the course back to its previous state ("${course.status_before_delete}"). It was deleted by ${course.deleted_by_name} — "${course.deletion_reason}".`,
            confirmText: "Restore course",
            cancelText: "Keep deleted",
            tone: "primary",
            testid: "admin-course-restore-confirm",
        });
        if (!ok) return;
        try {
            const { data } = await api.post(`/admin/course-review/courses/${course.id}/restore`);
            toast.success(`"${course.title}" restored to ${data.status}.`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't restore course.");
        }
    }, [load]);

    return (
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-8" data-testid="course-review-inbox">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-muted-foreground">Admin</div>
                    <h1 className="font-display text-3xl font-bold tracking-tight mt-0.5 flex items-center gap-2">
                        <GraduationCap size={28} weight="duotone" className="text-primary" />
                        Course Reviews
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Approve or request revisions on submissions, audit live courses, and pull them back to draft when corrections are needed.
                    </p>
                </div>
                <div className="grid grid-cols-4 gap-2">
                    <StatChip label="Pending" value={inbox.counts.pending} tone="amber" Icon={ClockCounterClockwise} />
                    <StatChip label="Published" value={inbox.counts.published} tone="emerald" Icon={CheckCircle} />
                    <StatChip label="Rejected" value={inbox.counts.rejected} tone="rose" Icon={XCircle} />
                    <StatChip label="Deleted" value={deleted.length} tone="slate" Icon={Trash} />
                </div>
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-6">
                <TabsList className="h-10 bg-secondary/60 border border-border">
                    <TabsTrigger value="pending" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="tab-pending">
                        <ClockCounterClockwise size={13} weight="duotone" /> Pending
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{inbox.counts.pending}</span>
                    </TabsTrigger>
                    <TabsTrigger value="published" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="tab-published">
                        <CheckCircle size={13} weight="duotone" /> Published
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">{published.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="rejected" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="tab-rejected">
                        <XCircle size={13} weight="duotone" /> Rejected
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-800">{rejected.length}</span>
                    </TabsTrigger>
                    <TabsTrigger value="deleted" className="text-xs font-bold uppercase tracking-wider gap-1.5" data-testid="tab-deleted">
                        <Trash size={13} weight="duotone" /> Deleted
                        <span className="ml-1 tabular-nums text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-700">{deleted.length}</span>
                    </TabsTrigger>
                </TabsList>

                {/* ── PENDING ─────────────────────────────────────────── */}
                <TabsContent value="pending" data-testid="pane-pending">
                    {loading ? (
                        <SkeletonPane />
                    ) : inbox.pending.length === 0 ? (
                        <EmptyState
                            title="Inbox zero"
                            message="No courses awaiting review right now. When a Certification Author submits a course, it'll land here."
                            testid="pending-empty"
                        />
                    ) : (
                        <div className="bg-card border border-border rounded-2xl overflow-hidden">
                            <RowHeader cols="1fr 170px 110px 140px" labels={["Course", "Author", "Submitted", ""]} />
                            {inbox.pending.map((c) => <PendingRow key={c.id} course={c} />)}
                        </div>
                    )}
                </TabsContent>

                {/* ── PUBLISHED ───────────────────────────────────────── */}
                <TabsContent value="published" data-testid="pane-published">
                    {loading ? (
                        <SkeletonPane />
                    ) : published.length === 0 ? (
                        <EmptyState
                            title="No live courses"
                            message="Courses that have been approved will appear here so you can audit or pull them back to draft."
                            testid="published-empty"
                        />
                    ) : (
                        <div className="bg-card border border-border rounded-2xl overflow-hidden">
                            <RowHeader cols="minmax(0,1fr) 150px 80px 80px 130px 180px" labels={["Course", "Author", "Enrolled", "Certified", "Published", ""]} />
                            {published.map((c) => (
                                <PublishedRow key={c.id} course={c} onUnpublish={() => handleUnpublish(c)} />
                            ))}
                        </div>
                    )}
                </TabsContent>

                {/* ── REJECTED ────────────────────────────────────────── */}
                <TabsContent value="rejected" data-testid="pane-rejected">
                    {loading ? (
                        <SkeletonPane />
                    ) : rejected.length === 0 ? (
                        <EmptyState
                            title="No rejections"
                            message="Rejected submissions will archive here with the feedback you left, so authors have a clear trail to revise against."
                            testid="rejected-empty"
                        />
                    ) : (
                        <div className="space-y-3">
                            {rejected.map((c) => <RejectedRow key={c.id} course={c} onDelete={() => handleAdminDelete(c)} />)}
                        </div>
                    )}
                </TabsContent>

                {/* ── DELETED (Recycle bin) ───────────────────────────── */}
                <TabsContent value="deleted" data-testid="pane-deleted">
                    {loading ? (
                        <SkeletonPane />
                    ) : deleted.length === 0 ? (
                        <EmptyState
                            title="No deleted courses"
                            message="When an Author or Admin deletes a course, it lands here with the deletion reason. You can restore it from here — content stays intact."
                            testid="deleted-empty"
                        />
                    ) : (
                        <div className="space-y-3">
                            {deleted.map((c) => (
                                <DeletedRow key={c.id} course={c} onRestore={() => handleRestore(c)} />
                            ))}
                        </div>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}


/* ═════════════════════════════════════════════════════════════════════
   Row + support components
   ═════════════════════════════════════════════════════════════════════ */

function StatChip({ label, value, tone, Icon }) {
    const cls = {
        amber:   "bg-amber-50 border-amber-200 text-amber-800",
        emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
        rose:    "bg-rose-50 border-rose-200 text-rose-800",
        slate:   "bg-slate-100 border-slate-200 text-slate-700",
    }[tone] || "bg-secondary border-border text-foreground";
    return (
        <div className={`px-3 py-2 rounded-xl border ${cls} min-w-24`} data-testid={`review-stat-${label.toLowerCase()}`}>
            <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider">
                <Icon size={9} weight="fill" /> {label}
            </div>
            <div className="text-xl font-black tabular-nums leading-none mt-1">{value}</div>
        </div>
    );
}


function RowHeader({ cols, labels }) {
    return (
        <div
            className="grid items-center px-4 py-3 border-b border-border bg-secondary/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground"
            style={{ gridTemplateColumns: cols }}
        >
            {labels.map((l, i) => (
                <div key={i} className={i === labels.length - 1 ? "text-right" : ""}>{l}</div>
            ))}
        </div>
    );
}


function PendingRow({ course }) {
    const submitter = course.latest_review?.submitter_note;
    return (
        <div
            className="grid items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors"
            style={{ gridTemplateColumns: "1fr 170px 110px 140px" }}
            data-testid={`review-row-${course.id}`}
        >
            <div className="min-w-0">
                <div className="font-semibold text-sm truncate" data-testid={`review-row-title-${course.id}`}>{course.title}</div>
                <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
                    <span>{course.module_count} module{course.module_count === 1 ? "" : "s"}</span>
                    <span>{course.lesson_count} lesson{course.lesson_count === 1 ? "" : "s"}</span>
                    {course.track && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.track}</span>}
                    {course.phase && <span className="px-1.5 py-0.5 rounded bg-secondary">{course.phase}</span>}
                </div>
                {submitter && (
                    <div className="mt-1 text-[11px] text-muted-foreground italic line-clamp-1">
                        &ldquo;{submitter}&rdquo;
                    </div>
                )}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5 truncate">
                <UserIcon size={11} weight="duotone" />
                <span className="truncate">{course.author_name || "Unknown"}</span>
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
                {timeAgo(course.submitted_at)}
            </div>
            <div className="text-right">
                <Link
                    to={`/admin/course-reviews/${course.id}`}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-md bg-primary text-primary-foreground text-xs font-bold no-underline hover:bg-primary/90 transition-colors"
                    data-testid={`review-row-open-${course.id}`}
                >
                    Review <ArrowRight size={11} weight="bold" />
                </Link>
            </div>
        </div>
    );
}


function PublishedRow({ course, onUnpublish }) {
    return (
        <div
            className="grid items-center px-4 py-3 border-b border-border last:border-b-0 hover:bg-secondary/30 transition-colors"
            style={{ gridTemplateColumns: "minmax(0,1fr) 150px 80px 80px 130px 180px" }}
            data-testid={`published-row-${course.id}`}
        >
            <div className="min-w-0 pr-3">
                <div className="font-semibold text-sm truncate" data-testid={`published-row-title-${course.id}`}>
                    {course.title}
                </div>
                <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground truncate">
                    <span className="shrink-0">{course.module_count}m · {course.lesson_count}l</span>
                    {course.track && <span className="shrink-0 px-1.5 py-0.5 rounded bg-secondary">{course.track}</span>}
                    {course.phase && <span className="shrink-0 px-1.5 py-0.5 rounded bg-secondary">{course.phase}</span>}
                </div>
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1.5 min-w-0">
                <UserIcon size={11} weight="duotone" className="shrink-0" />
                <span className="truncate">{course.author_name || "Unknown"}</span>
            </div>
            <div className="text-xs tabular-nums text-foreground flex items-center gap-1">
                <Users size={11} weight="duotone" className="text-muted-foreground" />
                {course.enrollment_count ?? 0}
            </div>
            <div className="text-xs tabular-nums text-foreground flex items-center gap-1">
                <Certificate size={11} weight="duotone" className="text-muted-foreground" />
                {course.cert_count ?? 0}
            </div>
            <div className="text-xs text-muted-foreground tabular-nums">
                {timeAgo(course.published_at)}
            </div>
            <div className="text-right flex items-center justify-end gap-2">
                <Link
                    to={`/admin/course-reviews/${course.id}`}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-border bg-background text-xs font-semibold no-underline hover:bg-secondary transition-colors"
                    data-testid={`published-row-view-${course.id}`}
                >
                    <Eye size={11} weight="duotone" /> View
                </Link>
                <button
                    type="button"
                    onClick={onUnpublish}
                    className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-xs font-bold hover:bg-amber-100 transition-colors"
                    data-testid={`published-row-unpublish-${course.id}`}
                >
                    <ArrowUUpLeft size={11} weight="bold" /> Unpublish
                </button>
            </div>
        </div>
    );
}


function RejectedRow({ course, onDelete }) {
    const feedback = course.latest_review?.feedback;
    const decidedBy = course.latest_review?.decider_name;
    return (
        <div
            className="bg-card border border-border rounded-2xl px-4 py-3.5 hover:bg-secondary/30 transition-colors"
            data-testid={`rejected-row-${course.id}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm" data-testid={`rejected-row-title-${course.id}`}>{course.title}</div>
                    <div className="flex items-center gap-3 mt-0.5 text-[11px] text-muted-foreground">
                        <span className="inline-flex items-center gap-1">
                            <UserIcon size={10} weight="duotone" />
                            {course.author_name || "Unknown"}
                        </span>
                        <span>Rejected {timeAgo(course.updated_at)}</span>
                        {decidedBy && <span>by {decidedBy}</span>}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Link
                        to={`/admin/course-reviews/${course.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-border bg-background text-xs font-semibold no-underline hover:bg-secondary transition-colors"
                        data-testid={`rejected-row-open-${course.id}`}
                    >
                        <Eye size={11} weight="duotone" /> View
                    </Link>
                    {onDelete && (
                        <button
                            type="button"
                            onClick={onDelete}
                            className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md border border-rose-200 bg-rose-50 text-rose-700 text-xs font-bold hover:bg-rose-100 transition-colors"
                            data-testid={`rejected-row-delete-${course.id}`}
                        >
                            <Trash size={11} weight="bold" /> Delete
                        </button>
                    )}
                </div>
            </div>
            {feedback && (
                <div className="mt-2 text-[12px] text-rose-900 bg-rose-50/70 border border-rose-200 rounded-lg px-3 py-2 leading-snug">
                    <div className="text-[9px] font-bold uppercase tracking-wider text-rose-700 mb-0.5">Feedback</div>
                    {feedback}
                </div>
            )}
        </div>
    );
}


function EmptyState({ title, message, testid }) {
    return (
        <div className="py-16 text-center border border-dashed border-border rounded-2xl" data-testid={testid || "review-empty-state"}>
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 border border-emerald-200 mx-auto mb-3 grid place-items-center">
                <CheckCircle size={26} weight="duotone" className="text-emerald-600" />
            </div>
            <h3 className="font-display text-lg font-bold">{title}</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{message}</p>
        </div>
    );
}


/* Row rendered inside the Deleted tab. Surfaces who deleted it, when,
   and why. Restore button flips the soft-delete flag server-side and
   removes the row from this view. */
function DeletedRow({ course, onRestore }) {
    const roleShort = (course.deleted_by_role || "").replace(/_/g, " ");
    return (
        <div
            className="bg-card border border-border rounded-2xl px-4 py-3.5"
            data-testid={`deleted-row-${course.id}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm" data-testid={`deleted-row-title-${course.id}`}>{course.title}</span>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-bold uppercase tracking-wider">
                            was {course.status_before_delete}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[11px] text-muted-foreground flex-wrap">
                        <span className="inline-flex items-center gap-1">
                            <UserIcon size={10} weight="duotone" />
                            Author: {course.author_name}
                        </span>
                        <span>Deleted {timeAgo(course.deleted_at)}</span>
                        <span>
                            by <span className="font-semibold text-foreground" data-testid={`deleted-row-actor-${course.id}`}>{course.deleted_by_name}</span>
                            {roleShort && <span className="uppercase text-[9px] tracking-wider ml-1">· {roleShort}</span>}
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={onRestore}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-md border border-primary/40 bg-primary/10 text-primary text-xs font-bold hover:bg-primary/20 transition-colors"
                        data-testid={`deleted-row-restore-${course.id}`}
                    >
                        <ArrowClockwise size={11} weight="bold" /> Restore
                    </button>
                </div>
            </div>
            {course.deletion_reason && (
                <div className="mt-2 text-[12px] text-slate-800 bg-slate-100/60 border border-slate-200 rounded-lg px-3 py-2 leading-snug" data-testid={`deleted-row-reason-${course.id}`}>
                    <div className="text-[9px] font-bold uppercase tracking-wider text-slate-500 mb-0.5">Reason for deletion</div>
                    {course.deletion_reason}
                </div>
            )}
        </div>
    );
}


function SkeletonPane() {
    return (
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
            {[0, 1, 2].map((i) => (
                <div key={i} className="px-4 py-4 border-b border-border last:border-b-0 flex items-center gap-4 animate-pulse">
                    <div className="flex-1 space-y-2">
                        <div className="h-3 bg-secondary rounded w-2/3" />
                        <div className="h-2.5 bg-secondary/70 rounded w-1/3" />
                    </div>
                    <div className="h-6 w-20 bg-secondary rounded-md" />
                </div>
            ))}
        </div>
    );
}
